import { isElectron } from '@utils/analytics/platform';

// pdfmake writes the generated ticket into a tab it opens ITSELF
// (`Document.prototype._openPdf` -> `window.open('', '_blank')`, pdfmake 0.2.7, loaded from the
// CDN in index.html:16). When that call returns null pdfmake throws the bare STRING
// "Open PDF in new window blocked by browser" — 592 of the 606 events of Sentry #157 in 90 days,
// 42 DISTINCT commerces in a single week. Two production paths make it return null:
//   1. Electron — the desktop shell denies EVERY popup
//      (plick-desktop/src/windows/main.ts:57, `setWindowOpenHandler` returns { action: 'deny' }),
//      so no tab will ever open there, no matter when we ask.
//   2. Browser — the popup blocker only honours window.open inside the transient activation of
//      the click, and the print hooks `await` getOrder()/generatePdf() first, so the activation
//      is already spent by then (worst on Mobile Safari, 39 events).
// Hence: we open the tab ourselves in the SAME tick as the click and hand it to pdfmake. When it
// still comes back empty the caller downloads the file instead of leaving a dead click behind.
//
// Do NOT add 'noopener'/'noreferrer' when hardening this later. By spec `noopener` makes
// window.open return null ON PURPOSE — which is exactly why navigationExternalUrl.ts:5 can use it
// (it never reads the handle). Here we need the handle to write the PDF into it, so that
// "hardening" would turn every web print into a download.
export const openPdfTab = (): Window | null => {
  // Inside the shell the handler does not only deny the popup: it forwards the URL to
  // `shell.openExternal()`, and pdfmake's URL is the empty string. Skipping the attempt also
  // spares the main process that pointless call on every print.
  if (isElectron()) return null;
  try {
    // jsdom and some blockers return undefined instead of null — normalize, or a `=== null`
    // check downstream would silently take the wrong branch.
    return window.open('', '_blank') || null;
  } catch {
    return null;
  }
};
